import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Bot, X, Send, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WELCOME = {
  role: "assistant",
  content:
    "Yo — I'm COACH, the DAFITEXCHANGE AI. Tell me what you train and I'll point you at the right gear from our 47-piece arsenal.",
};

export default function AssistantWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [productMap, setProductMap] = useState({});
  const scrollRef = useRef(null);
  const navigate = useNavigate();

  const [sessionId] = useState(() => {
    let sid = localStorage.getItem("dfx_ai_session");
    if (!sid) {
      sid = crypto.randomUUID();
      localStorage.setItem("dfx_ai_session", sid);
    }
    return sid;
  });

  useEffect(() => {
    fetch(`${API}/products`)
      .then((r) => r.json())
      .then((d) => setProductMap(Object.fromEntries(d.products.map((p) => [p.id, p]))))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!open) return;
    fetch(`${API}/ai/history/${sessionId}`)
      .then((r) => r.json())
      .then((d) => {
        if (d.messages?.length) setMessages([WELCOME, ...d.messages]);
      })
      .catch(() => {});
  }, [open, sessionId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "", recs: [] }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: text }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        let idx;
        while ((idx = buf.indexOf("\n\n")) >= 0) {
          const raw = buf.slice(0, idx);
          buf = buf.slice(idx + 2);
          if (!raw.startsWith("data: ")) continue;
          const data = raw.slice(6);
          if (data === "[DONE]") continue;
          const ev = JSON.parse(data);
          setMessages((prev) => {
            const next = [...prev];
            const last = { ...next[next.length - 1] };
            if (ev.type === "delta") last.content += ev.content;
            if (ev.type === "products") last.recs = ev.ids;
            if (ev.type === "error") last.content = ev.content;
            next[next.length - 1] = last;
            return next;
          });
        }
      }
    } catch {
      setMessages((prev) => {
        const next = [...prev];
        next[next.length - 1] = { role: "assistant", content: "Coach is catching breath — try again in a moment." };
        return next;
      });
    }
    setStreaming(false);
  };

  const renderContent = (content) =>
    content.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
      part.startsWith("**") ? (
        <strong key={i} className="text-[#D3FF24] font-semibold">{part.slice(2, -2)}</strong>
      ) : (
        <span key={i}>{part}</span>
      )
    );

  return (
    <>
      <motion.button
        data-testid="ai-assistant-button"
        onClick={() => setOpen(true)}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.6 }}
        className={`fixed bottom-5 right-5 z-40 items-center gap-2.5 bg-[#D3FF24] text-[#0A0A0C] px-4 py-3.5 text-[11px] font-bold tracking-[0.2em] shadow-[0_0_25px_rgba(211,255,36,0.25)] hover:bg-[#BEE61B] transition-colors ${open ? "hidden" : "flex"}`}
      >
        <Bot className="h-4.5 w-4.5" /> ASK COACH
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="ai-assistant-panel"
            initial={{ opacity: 0, y: 40, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.97 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-5 right-5 z-40 w-[calc(100vw-2.5rem)] max-w-[400px] h-[560px] max-h-[75vh] bg-[#111114] border border-white/10 flex flex-col shadow-2xl"
          >
            <div className="flex items-center justify-between px-5 h-14 border-b border-white/10 bg-[#0A0A0C]">
              <div className="flex items-center gap-2.5">
                <span className="p-1.5 bg-[#D3FF24] text-[#0A0A0C]"><Bot className="h-4 w-4" /></span>
                <div>
                  <p className="font-display font-extrabold text-xs tracking-[0.2em]">COACH AI</p>
                  <p className="text-[9px] tracking-[0.2em] text-white/35">POWERED BY CHATGPT</p>
                </div>
              </div>
              <button data-testid="ai-assistant-close" onClick={() => setOpen(false)} aria-label="Close assistant" className="p-1.5 text-white/60 hover:text-[#D3FF24] transition-colors">
                <X className="h-4.5 w-4.5" />
              </button>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-4" data-testid="ai-chat-messages">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    data-testid={`ai-message-${i}`}
                    className={`max-w-[85%] px-3.5 py-2.5 text-[13px] leading-relaxed ${
                      m.role === "user"
                        ? "bg-[#D3FF24] text-[#0A0A0C] font-medium"
                        : "bg-[#18181C] text-white/85 border border-white/[0.07]"
                    }`}
                  >
                    {m.content ? renderContent(m.content) : <Loader2 className="h-4 w-4 animate-spin text-[#D3FF24]" />}
                    {m.recs?.length > 0 && (
                      <div className="mt-3 space-y-2">
                        {m.recs.map((id) => {
                          const p = productMap[id];
                          if (!p) return null;
                          return (
                            <button
                              key={id}
                              data-testid={`ai-product-chip-${id}`}
                              onClick={() => { setOpen(false); navigate(`/shop?cat=${p.category.toLowerCase()}`); }}
                              className="w-full flex items-center gap-2.5 bg-[#0A0A0C] border border-white/10 hover:border-[#D3FF24]/60 p-2 transition-colors text-left"
                            >
                              <img src={p.image} alt={p.name} className="h-10 w-8 object-cover" />
                              <span className="flex-1 min-w-0">
                                <span className="block text-[11px] font-bold truncate">{p.name}</span>
                                <span className="block text-[10px] text-[#D3FF24]">${p.price.toFixed(2)}</span>
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-white/10 p-3 flex gap-2 bg-[#0A0A0C]">
              <input
                data-testid="ai-chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="What are you training for?"
                className="flex-1 bg-[#18181C] border border-white/10 px-3.5 py-2.5 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#D3FF24]/60"
              />
              <button
                data-testid="ai-chat-send"
                onClick={send}
                disabled={streaming || !input.trim()}
                aria-label="Send message"
                className="px-4 bg-[#D3FF24] text-[#0A0A0C] hover:bg-[#BEE61B] transition-colors disabled:opacity-40"
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
